import { useCallback, useRef } from "react";

export type TapLogFn = (
  level: "info" | "warn",
  message: string,
  meta: Record<string, unknown>
) => void;

/**
 * ATL-762: WebView pointer-event tap handler hook.
 *
 * Background: On certain Android Chrome WebViews (e.g. Samsung Galaxy +
 * Android 16), the browser fires `pointercancel` immediately after
 * `pointerdown` when the element is inside a scrollable region —
 * suppressing both `pointerup` and `click`. The button looks enabled
 * but does nothing.
 *
 * Strategy:
 *
 * 1. Primary fix: `touch-action: manipulation`. Tells the browser this
 *    element is never part of a scroll/pan gesture, so the false
 *    pointercancel should never fire to begin with. This is the
 *    standard, spec-compliant solution.
 *
 * 2. Defensive fallback: `onPointerUp` fires the action if the finger
 *    barely moved. This handles WebViews where `onClick` itself fails
 *    to fire even though `pointerup` does. Dedup'd against `onClick`
 *    via `clickFiredRef` so we never double-fire in the common path.
 *
 * 3. Optional recover: when `recoverTapOnPointerCancel` is set, a quick
 *    pointercancel with minimal movement is treated as a tap (Nubank shop
 *    CTAs only — see ATL-762 follow-up).
 *
 * See MDN pointercancel event, React issue #12560.
 */

const TAP_THRESHOLD_PX = 10;
const TAP_DEDUP_MS = 400;
const POINTER_CANCEL_RECOVER_MS = 250;

/** True if the pointer barely moved between start and the given event. */
function isTap(
  e: React.PointerEvent,
  start: { x: number; y: number } | null
): boolean {
  if (!start) return false;
  const dx = Math.abs(e.clientX - start.x);
  const dy = Math.abs(e.clientY - start.y);
  return dx < TAP_THRESHOLD_PX && dy < TAP_THRESHOLD_PX;
}

export interface UseTapHandlersOptions {
  /** The action to perform when the user taps the element. Receives the click event when fired from onClick. */
  onTap: (clickEvent?: React.MouseEvent) => void;
  /**
   * When true, the action is not fired. Pointer events still propagate
   * but `handleAction` returns early.
   */
  disabled?: boolean;
  /**
   * Used as a prefix in diagnostic log events (e.g. "flight_shop_cta"
   * produces "flight_shop_cta_pointerdown", "flight_shop_cta_pointerup_tap",
   * etc.). Logs are only emitted when both this and `onLog` are provided.
   */
  logName?: string;
  /** Emits Datadog diagnostics when paired with `logName`. Keeps utilities free of `@hopper-b2b/api`. */
  onLog?: TapLogFn;
  /**
   * When true, a quick pointercancel with minimal movement fires the action.
   * Use only on primary forward-navigation CTAs (shop continue, not dismiss).
   */
  recoverTapOnPointerCancel?: boolean;
}

export interface TapHandlers {
  style: { touchAction: "manipulation" };
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

export function useTapHandlers({
  onTap,
  disabled = false,
  logName,
  onLog,
  recoverTapOnPointerCancel = false,
}: UseTapHandlersOptions): TapHandlers {
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const pointerDownAt = useRef<number | null>(null);
  const clickFiredRef = useRef(false);

  const handleAction = useCallback(
    (clickEvent?: React.MouseEvent) => {
      if (disabled) return;
      if (clickFiredRef.current) return;
      clickFiredRef.current = true;
      setTimeout(() => {
        clickFiredRef.current = false;
      }, TAP_DEDUP_MS);
      onTap(clickEvent);
    },
    [disabled, onTap]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      pointerStart.current = { x: e.clientX, y: e.clientY };
      pointerDownAt.current = performance.now();
      clickFiredRef.current = false;
      if (logName && onLog) {
        onLog("info", `[${logName}] pointerDown`, {
          event: `${logName}_pointerdown`,
          disabled,
        });
      }
    },
    [disabled, logName, onLog]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const tap = isTap(e, pointerStart.current);
      pointerStart.current = null;
      pointerDownAt.current = null;
      if (!tap) return;
      if (logName && onLog) {
        onLog("info", `[${logName}] pointerUp tap`, {
          event: `${logName}_pointerup_tap`,
          disabled,
        });
      }
      handleAction();
    },
    [disabled, handleAction, logName, onLog]
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      const start = pointerStart.current;
      const tap = isTap(e, start);
      const quick =
        pointerDownAt.current != null &&
        performance.now() - pointerDownAt.current < POINTER_CANCEL_RECOVER_MS;

      if (recoverTapOnPointerCancel && tap && quick && !disabled) {
        pointerStart.current = null;
        pointerDownAt.current = null;
        if (logName && onLog) {
          onLog("warn", `[${logName}] pointerCancel recovered as tap`, {
            event: `${logName}_pointercancel_recovered`,
            disabled,
          });
        }
        handleAction();
        return;
      }

      pointerStart.current = null;
      pointerDownAt.current = null;
      if (logName && onLog) {
        onLog("warn", `[${logName}] pointerCancel observed`, {
          event: `${logName}_pointercancel`,
          disabled,
        });
      }
    },
    [disabled, handleAction, logName, onLog, recoverTapOnPointerCancel]
  );

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      handleAction(e);
    },
    [handleAction]
  );

  return {
    style: { touchAction: "manipulation" },
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onClick,
  };
}
